// Raw fetch wrapper for the public, read-only fridge storefront. The endpoint
// is unauthenticated; a 404 means the academy slug is unknown. An academy that
// exists but has not activated its fridge answers `enabled: false` with an
// empty product list, so the page renders the same empty state either way.
import { parseApiError } from 'app/javascript/services/api';

const PUBLIC_PATH = '/api/public/companies';

export interface FridgeProduct {
  id: number;
  name: string;
  price_cents: number;
  photo_url: string | null;
  display_quantity: number;
}

export interface FridgeCompany {
  name: string;
  slug: string;
  logo_url: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  phone: string | null;
}

export interface FridgeResponse {
  enabled: boolean;
  company: FridgeCompany;
  products: FridgeProduct[];
}

export interface FridgeOptions {
  // Legacy compatibility for clients talking to a backend from before the
  // storefront always kept sold-out products in its catalog.
  includeOutOfStock?: boolean;
}

// One line of a guest sale, snapshotted at purchase time.
export interface FridgeSaleItem {
  id: number;
  name: string;
  unit_price_cents: number;
  quantity: number;
  total_cents: number;
}

export interface FridgeSale {
  id: number;
  status: 'pending' | 'paid' | 'withdrawn' | 'canceled';
  total_cents: number;
  items: FridgeSaleItem[];
  payment: {
    status: string;
    amount_cents: number;
    pix_payload: string;
    pix_key: string;
    reported_at: string | null;
  } | null;
}

// Guest checkout errors carry a user-facing message from the backend
// (insufficient stock, pending-sale cap, disabled fridge). Distinct class so
// the page can tell "show this message" apart from unexpected failures.
export class FridgeSaleError extends Error {}

// One key per checkout attempt. randomUUID needs a secure context, which the
// storefront always has in production; the fallback keeps a plain-http preview
// from breaking checkout entirely.
export function newCheckoutKey(): string {
  if (typeof crypto?.randomUUID === 'function') return crypto.randomUUID();
  return `k-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

async function parseSale(response: Response): Promise<FridgeSale> {
  if (!response.ok) {
    throw new FridgeSaleError(
      await parseApiError(response, 'Não foi possível registrar a compra. Tente novamente.'),
    );
  }
  return response.json();
}

export const fridgeService = {
  async get(slug: string, options: FridgeOptions = {}): Promise<FridgeResponse> {
    const query = options.includeOutOfStock ? '?include_out_of_stock=true' : '';
    const response = await fetch(`${PUBLIC_PATH}/${slug}/fridge${query}`);
    if (!response.ok) throw new Error('Failed to fetch fridge');
    return response.json();
  },

  // Guest checkout: no auth — the backend sets a signed, httponly guest
  // cookie on the first purchase and scopes every later lookup by it. The
  // idempotency key belongs to the checkout attempt, not to the request: a
  // retry after a lost response answers with the sale already registered
  // instead of buying the shelf twice.
  async createSale(
    slug: string,
    items: Array<{ product_id: number; quantity: number }>,
    idempotencyKey: string,
  ): Promise<FridgeSale> {
    const response = await fetch(`${PUBLIC_PATH}/${slug}/fridge/sales`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items, idempotency_key: idempotencyKey }),
    });
    return parseSale(response);
  },

  async getSale(slug: string, id: number): Promise<FridgeSale> {
    const response = await fetch(`${PUBLIC_PATH}/${slug}/fridge/sales/${id}`);
    return parseSale(response);
  },

  async reportPayment(slug: string, id: number): Promise<FridgeSale> {
    const response = await fetch(`${PUBLIC_PATH}/${slug}/fridge/sales/${id}/payment_report`, {
      method: 'POST',
    });
    return parseSale(response);
  },
};
